import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toPng } from 'html-to-image';
import { MARK_SVG } from '../data/assets';
type Variant = {
  id: string;
  label: string;
  note: string;
  background: string;
  /** Mark inset as a share of the tile, so it holds at any export size. */
  padding: number;
};

/**
 * A favicon is read at 16px in a browser tab, so it can only carry the mark —
 * the wordmark would be illegible. Two treatments, because a transparent mark
 * vanishes against a dark tab strip: a white tile reads on every theme, a blue
 * tile reads as brand. Both are square with generous padding, since browsers
 * and phone home screens crop the corners differently.
 */
/**
 * The mark is a thin stroke, so at 16px it needs almost the whole tile and a
 * solid ground behind it. Padding is kept small: anything more and the tab
 * shows a faint sliver instead of the S.
 */
const VARIANTS: Variant[] = [{
  id: 'blue',
  label: 'Blue tile · recommended',
  note: 'White mark on brand blue. Reads clearly at tab size on light and dark browsers.',
  background: '#0E6FA3',
  padding: 0.1
}, {
  id: 'light',
  label: 'White tile',
  note: 'Dark mark on white. Quieter, but can look faint on a light tab strip.',
  background: '#FFFFFF',
  padding: 0.08
}];

/** Rounded corners, as a share of the tile, so it reads as an app icon rather than a square chip. */
const RADIUS = 0.22;
const SIZE = 512;
export function FaviconCard() {
  const refs = useRef<Record<string, HTMLDivElement | null>>({});
  const [state, setState] = useState<'idle' | 'working' | 'done' | 'error'>('idle');
  async function download(id: string) {
    const node = refs.current[id];
    if (!node) return;
    setState('working');
    try {
      const url = await toPng(node, {
        width: SIZE,
        height: SIZE,
        pixelRatio: 1,
        cacheBust: true,
        // The tile is shrunk on screen for display. Without this, the export
        // keeps that shrink and draws a small tile in the top-left corner of
        // an otherwise empty image.
        style: {
          transform: 'none'
        }
      });
      const a = document.createElement('a');
      a.href = url;
      a.download = 'favicon.png';
      a.click();
      setState('done');
    } catch {
      setState('error');
    }
  }
  return <div style={{
    minHeight: '100vh',
    background: '#E9EDF1',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 36,
    padding: 40
  }} data-id="element-441" data-mp="1aez06hrvxukj">
      <div style={{
      display: 'flex',
      gap: 48,
      flexWrap: 'wrap',
      justifyContent: 'center'
    }} data-id="element-442" data-mp="vq2x6z4or7qs">
        {VARIANTS.map(variant => <div key={variant.id} style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 18
      }} data-id="element-443" data-mp="rqkco4gs0yoh">
            {/* Rendered at full export size, scaled down for display only, so
                what you download is exactly what you see. */}
            <div style={{
          width: 200,
          height: 200,
          position: 'relative'
        }} data-id="element-444" data-mp="2tddgjr7k2dz0">
              <div ref={node => {
            refs.current[variant.id] = node;
          }} style={{
            width: SIZE,
            height: SIZE,
            background: variant.background,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: `scale(${200 / SIZE})`,
            transformOrigin: 'top left',
            border: variant.id === 'light' ? '6px solid #E3E7EC' : 'none',
            borderRadius: SIZE * RADIUS,
            boxSizing: 'border-box'
          }} data-id="element-445" data-mp="2ycgllbnjck6z">
                <img src={MARK_SVG} alt="" style={{
              width: `${100 - variant.padding * 200}%`,
              display: 'block',
              filter: variant.id === 'blue' ? 'brightness(0) invert(1)' : 'none'
            }} data-id="element-446" data-mp="1rxpgevrdqd75" />
              </div>
            </div>

            {/* Tab-strip preview at true 16px, which is the only size that
                actually decides whether a favicon works. */}
            <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: '#F1F3F5',
          border: '1px solid #DDE2E8',
          padding: '7px 12px',
          borderRadius: 6
        }} data-id="element-447" data-mp="1hsz9scl16kwl">
              <span style={{
            width: 16,
            height: 16,
            flex: 'none',
            background: variant.background,
            borderRadius: 16 * RADIUS,
            boxShadow: variant.id === 'light' ? 'inset 0 0 0 1px #E3E7EC' : 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden'
          }} data-id="element-448" data-mp="25hiiuubwn1pq">
                <img src={MARK_SVG} alt="" style={{
              width: `${100 - variant.padding * 200}%`,
              display: 'block',
              filter: variant.id === 'blue' ? 'brightness(0) invert(1)' : 'none'
            }} data-id="element-449" data-mp="2mrmgtyl4qc3e" />
              </span>
              <span style={{
            fontFamily: 'var(--mono)',
            fontSize: 11,
            letterSpacing: '.04em',
            color: 'var(--ink-2)'
          }} data-id="element-450" data-mp="234oeekg75dc0">
                meetsynthia.ai
              </span>
            </div>

            <div style={{
          textAlign: 'center',
          maxWidth: 240
        }} data-id="element-451" data-mp="3ji54l1zjpkwb">
              <div style={{
            fontFamily: 'var(--mono)',
            fontSize: 11,
            letterSpacing: '.1em',
            textTransform: 'uppercase',
            color: 'var(--ink)'
          }} data-id="element-452" data-mp="3jglkt1eokrps">
                {variant.label}
              </div>
              <p style={{
            fontSize: 12.5,
            lineHeight: 1.5,
            color: 'var(--mut)',
            marginTop: 6
          }} data-id="element-453" data-mp="3d1d3qp5wczt8">
                {variant.note}
              </p>
            </div>

            <button type="button" className={variant.id === 'blue' ? 'btn g' : 'btn o'} onClick={() => download(variant.id)} disabled={state === 'working'} data-id="element-454" data-mp="385fuf2t1wtpy">
              {state === 'working' ? 'Preparing\u2026' : 'Download favicon.png'}
            </button>
          </div>)}
      </div>

      <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 16
    }} data-id="element-455" data-mp="3r1edyv81xjld">
        <span style={{
        fontFamily: 'var(--mono)',
        fontSize: 12,
        letterSpacing: '.05em',
        color: state === 'error' ? '#b8492e' : 'var(--ink-2)'
      }} data-id="element-456" data-mp="sc7aq3xysmju">
          {state === 'done' ? 'Saved to your Downloads \u2014 upload it in Publish \u2192 Site Metadata' : state === 'error' ? 'Couldn\u2019t render \u2014 try once more' : `${SIZE}\u00d7${SIZE} \u00b7 saves to your Downloads folder`}
        </span>
        <Link className="btn o" to="/" data-id="element-457" data-mp="19v7b3ovtoi48">
          &larr; Back to the site
        </Link>
      </div>
    </div>;
}